/**
 * API Client for Audit Logs
 * Handles HTTP requests for managing audit logs and statistics
 */
import api from './api';

export type AuditAction = 'CREATE' | 'UPDATE' | 'DELETE' | 'LOGIN' | 'LOGOUT' | 'EXPORT' | 'SHARE';

export interface AuditLog {
  id: string;
  tenantId: string;
  userId?: string;
  action: AuditAction;
  entityType: string;
  entityId: string;
  oldValues?: Record<string, any>;
  newValues?: Record<string, any>;
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
  User?: {
    id: string;
    email: string;
    fullName: string;
  };
}

export interface AuditStats {
  totalLogs: number;
  byAction: Array<{
    action: AuditAction;
    count: number;
  }>;
  byUser: Array<{
    userId: string | null;
    count: number;
  }>;
}

export interface GetAuditLogsParams {
  page?: number;
  limit?: number;
  action?: AuditAction;
  userId?: string;
  resourceType?: string;
  startDate?: string;
  endDate?: string;
}

export interface AuditLogsResponse {
  success: boolean;
  data: AuditLog[];
  pagination: {
    total: number;
    totalPages: number;
    currentPage: number;
    limit: number;
  };
  message?: string;
}

export interface AuditStatsResponse {
  success: boolean;
  data: AuditStats;
  message?: string;
}

export interface AuditResourceHistoryResponse {
  success: boolean;
  data: AuditLog[];
  count: number;
  message?: string;
}

/**
 * API client for Audit Logs
 */
export const auditLogsAPI = {
  /**
   * Get all audit logs with optional filters
   */
  async getLogs(params?: GetAuditLogsParams): Promise<{ logs: AuditLog[]; pagination: any }> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.action) queryParams.append('action', params.action);
    if (params?.userId) queryParams.append('userId', params.userId);
    if (params?.resourceType) queryParams.append('resourceType', params.resourceType);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const response = await api.get<AuditLogsResponse>(
      `/audit/logs?${queryParams.toString()}`
    );
    return {
      logs: response.data,
      pagination: response.pagination,
    };
  },

  /**
   * Get audit statistics
   */
  async getStats(startDate?: string, endDate?: string): Promise<AuditStats> {
    const queryParams = new URLSearchParams();
    if (startDate) queryParams.append('startDate', startDate);
    if (endDate) queryParams.append('endDate', endDate);

    const response = await api.get<AuditStatsResponse>(
      `/audit/stats?${queryParams.toString()}`
    );
    return response.data;
  },

  /**
   * Get audit history for a specific resource
   */
  async getResourceHistory(resourceType: string, resourceId: string): Promise<AuditLog[]> {
    const response = await api.get<AuditResourceHistoryResponse>(
      `/audit/resource/${resourceType}/${resourceId}`
    );
    return response.data;
  },

  /**
   * Get audit history for a specific user
   */
  async getUserHistory(userId: string, limit?: number): Promise<AuditLog[]> {
    const queryParams = new URLSearchParams();
    if (limit) queryParams.append('limit', limit.toString());

    const response = await api.get<AuditResourceHistoryResponse>(
      `/audit/user/${userId}?${queryParams.toString()}`
    );
    return response.data;
  },
};
